import { applicationLogger } from "../logging/logger";
import { AureliaApplicationSettings } from "../models/aurelia-application-settings";
import { autoinject } from "aurelia-framework";
import { DataProcessorSettings } from "../models/data-processor-settings";
import { HttpClient } from "aurelia-fetch-client";
import { IApiError } from "../models/application-error";
import { Kiosk, KioskSetting } from "../models/kiosk";
import { KioskClientSecretsRequest } from "../models/kiosk-client-secrets-request";
import { KioskClientSecretsResponse } from "../models/kiosk-client-secrets-response"
import { DataRequestViewModel } from "../models/data-request-view-model";
import { KioskMeasurementViewModel } from "../models/kiosk-measurement-view-model";
import { LogDates } from "../models/log-dates";
import { MagensaSettings } from "../models/magensa-settings";
import { OpenIdConnect } from 'aurelia-open-id-connect';
import { ProcessedTransactionRequest } from "../models/processed-transaction-request";
import { ProcessedTransactionByLastFourRequest } from "../models/processed-transaction-by-last-four-request";
import { TransactionBatchesRequest } from "../models/transaction-batches-request";
import { Tenant } from "../models/tenant";
import { TenantManagement } from "../models/tenant-management";
import { TenantUser } from "../models/user";
import { Transaction } from "../models/transaction";
import { TransactionBatch } from "../models/transaction-batch";
import { TransactionDetail } from "../models/transaction-detail";
import { TransactionStatus } from "../models/transaction-status-enum";
import { WebApiResponse } from "../models/web-api-response";
import * as moment from 'moment';
import { TicketDatumViewModel } from "../models/ticket-datum-view-model";

@autoinject
export class AdminApi {

    fetching: boolean;

    constructor(private applicationSettings: AureliaApplicationSettings,
        private httpClient: HttpClient,
        private openIdConnect: OpenIdConnect) {
    }

    private async configureHttpClient() {
        const user = await this.openIdConnect.getUser();
        const accessToken = user.access_token;

        this.httpClient.configure(config => {
           config.withDefaults({
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    "Authorization": `Bearer ${accessToken}`
                }
            });
        });
    }

    private async configureHttpClientWithTenantIdHeader(tenantId: string) {
        const user = await this.openIdConnect.getUser();
        const accessToken = user.access_token;

        this.httpClient.configure(config => {
            config.withDefaults({
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    "Authorization": `Bearer ${accessToken}`,
                    "Sanistar-Tenant-Id": tenantId
                }
            });
        });
    }

    private downloadObjectUrl(url: string, filename: string) {
        const anchor = document.createElement("a");
        anchor.download = filename;
        anchor.href = url;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
    }

    private downloadBlob(blob: Blob, filename: string) {
        const url = URL.createObjectURL(blob);
        this.downloadObjectUrl(url, filename);
        URL.revokeObjectURL(url);
    }


    addKiosk(kiosk: Kiosk): Promise<WebApiResponse<Kiosk>> {
        this.configureHttpClient();
        return this.httpClient.post(`api/tenant/{kiosk.tenantId}/kiosk`, JSON.stringify(kiosk))
            .then(result => result.json() as Promise<WebApiResponse<Kiosk>>)
            .then(response => {
                this.fetching = false;
                return response;
            }).catch(error => {
                this.fetching = false;
                applicationLogger.error(error);
                return null;
            });
    }

    addOrUpdateUser(user: TenantUser): Promise<WebApiResponse<TenantUser>> {
        this.configureHttpClient();
        return this.httpClient.post(`api/tenant/${user.tenantId}/user`, JSON.stringify(user))
            .then(result => result.json() as Promise<WebApiResponse<TenantUser>>)
            .then(response => {
                this.fetching = false;
                return response;
            }).catch(error => {
                this.fetching = false;
                applicationLogger.error(error);
                return null;
            });
    }

    apiLogout(): Promise<WebApiResponse<string>> {
        this.configureHttpClient();
        try {
           return this.httpClient.post(`api/Account/Logout`, JSON.stringify(""))
                .then(result => result.json() as Promise<WebApiResponse<string>>)
                .then(response => {
                    this.fetching = false;
                    return response;
                }).catch(error => {
                    this.fetching = false;
                    applicationLogger.error(error);
                    var webApiResponse = new WebApiResponse<string>();
                    webApiResponse.success = false;
                    webApiResponse.message = error;
                    return webApiResponse;
                });

            
        }
        catch (ex) {
            return null;
        }
       
    }


    searchForUsers(query: string): Promise<WebApiResponse<Array<TenantUser>>>
    {
        this.fetching = true;
        this.configureHttpClient();
        return this.httpClient.get(`api/users/search?query=${query}`)
            .then(result => result.json() as Promise<WebApiResponse<Array<TenantUser>>>)
            .then(response =>
            {
                this.fetching = false;
                return response;
            }).catch(error =>
            {
                this.fetching = false;
                applicationLogger.error(error);
                return null;
            });
    }

    deleteUser(user: TenantUser): Promise<WebApiResponse<string>>
    {
        this.fetching = true;
        this.configureHttpClient();
        return this.httpClient.delete(`api/tenant/${user.tenantId}/user/${user.id}`)
            .then(result => result.json() as Promise<WebApiResponse<string>>)
            .then(response =>
            {
                this.fetching = false;
                return response;
            }).catch(error =>
            {
                this.fetching = false;
                applicationLogger.error(error);
                return null;
            });
    }

    addTenant(tenant: Tenant): Promise<WebApiResponse<Tenant>> {
        this.configureHttpClient();
        return this.httpClient.post(`api/tenants`, JSON.stringify(tenant))
            .then(result => result.json() as Promise<WebApiResponse<Tenant>>)
            .then(response => {
                this.fetching = false;
                return response;
            }).catch(error => {
                this.fetching = false;
                applicationLogger.error(error);
                return null;
            });
    }

    async downloadDeclinedReport(format: string, batch: TransactionBatch): Promise<void> {
        this.fetching = true;
        await this.configureHttpClient();
        let uri = `api/reports/declined?batchId=${batch.id}`;
        return this.httpClient.fetch(uri,
                {
                    method: "GET",
                    headers: {
                        "Accept": format === "csv" ? "text/csv" : "application/pdf"
                    }
                })
            .then(result => result.blob())
            .then(blob => this.downloadBlob(blob, `${batch.name} declined report.${format}`))
            .then(response => {
                this.fetching = false;
                return response;
            })
            .catch(error => {
                const apiError = error as IApiError;
                applicationLogger.error(apiError.message);
                this.fetching = false;
                return null;
            });
    }

    async downloadKioskLog(kioskId: string, kioskName: string, logType: string, date: Date, tenantId: string): Promise<void> {
        this.fetching = true;
        await this.configureHttpClientWithTenantIdHeader(tenantId);
        return this.httpClient.fetch(`api/kiosks/${kioskId}/logs/${logType}/${moment(date).format("YYYY-MM-DD")}`,
            {
                method: "GET",
                headers: {
                    "Accept": "text/plain"
                }
            })
            .then(result => result.blob())
            .then(blob => this.downloadBlob(blob, `${kioskName} ${logType} ${moment(date).format("YYYY-MM-DD")}.log`))
            .then(response => {
                this.fetching = false;
                return response;
            })
            .catch(error => {
                const apiError = error as IApiError;
                applicationLogger.error(apiError.message);
                this.fetching = false;
                return null;
            });
    }

    async downloadKioskLogBundle(kioskId: string, kioskName: string, logType: string, tenantId: string): Promise<void> {
        this.fetching = true;
        await this.configureHttpClientWithTenantIdHeader(tenantId);
        return this.httpClient.fetch(`api/kiosks/${kioskId}/logs/${logType}`,
            {
                method: "GET",
                headers: {
                    "Accept": "application/zip"
                }
            })
            .then(result => result.blob())
            .then(blob => this.downloadBlob(blob, `${kioskName} ${logType}.zip`))
            .then(response => {
                this.fetching = false;
                return response;
            })
            .catch(error => {
                const apiError = error as IApiError;
                applicationLogger.error(apiError.message);
                this.fetching = false;
                return null;
            });
    }

    async downloadReprocessedReport(tenantId: string, tenantName: string, reportName: string): Promise<void> {
        this.fetching = true;
        await this.configureHttpClientWithTenantIdHeader(tenantId);
        return this.httpClient.fetch(`api/transactions/resubmitted/${reportName}?tenantId=${tenantId}`,
                {
                    method: "GET",
                    headers: {
                        "Accept": "text/plain"
                    }
                })
            .then(result => result.blob())
            .then(blob => this.downloadBlob(blob, `${tenantName} ${reportName}.csv`))
            .then(response => {
                this.fetching = false;
                return response;
            })
            .catch(error => {
                const apiError = error as IApiError;
                applicationLogger.error(apiError.message);
                this.fetching = false;
                return null;
            });
    }

    async downloadPurchaseDetailsReport(format: string, startDate: string, endDate: string, summarize: string, tenantId: string): Promise<void> {
        this.fetching = true;
        await this.configureHttpClient();
        let uri = `api/reports/purchaseDetail?startDate=${moment(startDate).format('YYYY-MM-DD')}&endDate=${moment(endDate).format('YYYY-MM-DD')}&summarize=${summarize}`;
        if (tenantId) {
            uri = uri + `&tenantId=${tenantId}`;
        }

        const mimeTypes: Record<typeof format, string> = {
            csv: "text/csv",
            xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            pdf: "application/pdf"
        };


        return this.httpClient.fetch(uri,
                {
                    method: "GET",
                    headers: {
                        Accept: mimeTypes[format]
                    }

                })
            .then(result => result.blob())
            .then(blob => this.downloadBlob(blob, `Purchase Details Report (${summarize === "1" ? "Processed Date": "Transaction Date"}) ${moment(startDate).format('YYYY-MM-DD')} to ${moment(endDate).format('YYYY-MM-DD')}.${format}`))
            .then(response => {
                this.fetching = false;
                return response;
            })
            .catch(error => {
                const apiError = error as IApiError;
                applicationLogger.error(apiError.message);
                this.fetching = false;
                return null;
            });
    }

    async downloadSummaryReport(format: string, startDate: string, endDate: string, summarize: string, tenantId: string): Promise<void> {
        this.fetching = true;
        await this.configureHttpClient();
        let uri = `api/reports/summary?startDate=${moment(startDate).toISOString(true)}&endDate=${moment(endDate).toISOString(true)}&summarize=${summarize}`;
        if (tenantId) {
            uri = uri + `&tenantId=${tenantId}`;
        }
        return this.httpClient.fetch(uri,
                {
                    method: "GET",
                    headers: {
                        "Accept": format === "csv" ? "text/csv" : "application/pdf"
                    }
                })
            .then(result => result.blob())
            .then(blob => this.downloadBlob(blob, `${summarize === "0" ? "Transaction" : "Processed"} Summary Report ${moment(startDate).format('YYYY-MM-DD')} to ${moment(endDate).format('YYYY-MM-DD')}.${format}`))
            .then(response => {
                this.fetching = false;
                return response;
            })
            .catch(error => {
                const apiError = error as IApiError;
                applicationLogger.error(apiError.message);
                this.fetching = false;
                return null;
            });
    }

    async downloadTransactionLog(transactionId: string, logName: string, tenantId: string): Promise<void> {
        this.fetching = true;
        await this.configureHttpClientWithTenantIdHeader(tenantId);
        return this.httpClient.fetch(`api/transactions/${transactionId}/logs/${logName}`,
                {
                    method: "GET",
                    headers: {
                        "Accept": "text/plain"
                    }
                })
            .then(result => result.blob())
            .then(blob => this.downloadBlob(blob, `${transactionId} ${logName}.log`))
            .then(response => {
                this.fetching = false;
                return response;
            })
            .catch(error => {
                const apiError = error as IApiError;
                applicationLogger.error(apiError.message);
                this.fetching = false;
                return null;
            });
    }

    enableTransactionProcessing(tenantId: string, enable: boolean): Promise<WebApiResponse<Tenant>> {
        this.fetching = true;
        this.configureHttpClient();
        return this.httpClient.get(`api/magensa/tenant/${tenantId}/${enable}`)
            .then(result => result.json() as Promise<WebApiResponse<Tenant>>)
            .then(response => {
                this.fetching = false;
                return response;
            }).catch(error => {
                this.fetching = false;
                applicationLogger.error(error);
                return null;
            });
    }

    async enumerateResubmitLogs(tenantId: string): Promise<WebApiResponse<Array<string>>> {
        this.fetching = true;
        await this.configureHttpClient();
        return this.httpClient.get(`api/transactions/resubmitted?tenantId=${tenantId}`)
            .then(result => result.json() as Promise<WebApiResponse<string>>)
            .then(response => {
                this.fetching = false;
                return response;
            }).catch(error => {
                this.fetching = false;
                applicationLogger.error(error);
                return null;
            });
    }

    async getKioskSettings(kioskId: string): Promise<WebApiResponse<Array<KioskSetting>>> {
        this.fetching = true;
        await this.configureHttpClient();
        return this.httpClient.get(`api/allsettings/${kioskId}`)
            .then(result => result.json() as Promise<WebApiResponse<Array<KioskSetting>>>)
            .then(response => {
                this.fetching = false;
                return response;
            }).catch(error => {
                this.fetching = false;
                applicationLogger.error(error);
                return null;
            });
    }

    getKioskMeasurementData(graphRequest: DataRequestViewModel): Promise<WebApiResponse<Array<KioskMeasurementViewModel>>> {
        return this.httpClient.fetch("api/Kiosk/Measurements",
            {
                method: "POST",
                body: JSON.stringify(graphRequest),
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json"
                }
            })
            .then(result => result.json() as Promise<WebApiResponse<Array<KioskMeasurementViewModel>>>)
            .then(response => { return response; })
            .catch(error => {
                const apiError = error as IApiError;
                applicationLogger.error(apiError.message);
                return null;
            });
    }

    getTicketStockData(graphRequest: DataRequestViewModel): Promise<WebApiResponse<Array<TicketDatumViewModel>>> {
        return this.httpClient.fetch("api/Kiosk/Tickets",
            {
                method: "POST",
                body: JSON.stringify(graphRequest),
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json"
                }
            })
            .then(result => result.json() as Promise<WebApiResponse<Array<TicketDatumViewModel>>>)
            .then(response => { return response; })
            .catch(error => {
                const apiError = error as IApiError;
                applicationLogger.error(apiError.message);
                return null;
            });
    }


    async generateClientSecret(kioskId: string): Promise<WebApiResponse<string>> {
        this.fetching = true;
        await this.configureHttpClient();
        return this.httpClient.get(`api/settings/secret/${kioskId}`)
            .then(result => result.json() as Promise<WebApiResponse<string>>)
            .then(response => {
                this.fetching = false;
                return response;
            }).catch(error => {
                this.fetching = false;
                applicationLogger.error(error);
                return null;
            });
    }

    async getDataProcessorSettings(): Promise<WebApiResponse<DataProcessorSettings>> {
        this.fetching = true;
        await this.configureHttpClient();
        return this.httpClient.get(`api/settings/dataprocessor`)
            .then(result => result.json() as Promise<WebApiResponse<DataProcessorSettings>>)
            .then(response => {
                this.fetching = false;
                return response;
            }).catch(error => {
                this.fetching = false;
                applicationLogger.error(error);
                return null;
            });
    }

    async getKioskLogDates(kioskId: string, tenantId: string): Promise<WebApiResponse<LogDates>> {
        this.fetching = true;
        await this.configureHttpClientWithTenantIdHeader(tenantId);
        return this.httpClient.get(`api/kiosks/${kioskId}/logs`)
            .then(result => result.json() as Promise<WebApiResponse<LogDates>>)
            .then(response => {
                this.fetching = false;
                return response;
            }).catch(error => {
                this.fetching = false;
                applicationLogger.error(error);
                return null;
            });
    }

    async getPaymentSettings(kioskId: string): Promise<WebApiResponse<MagensaSettings>> {
        this.fetching = true;
        await this.configureHttpClient();
        return this.httpClient.get(`api/magensa/kiosk/${kioskId}`)
            .then(result => result.json() as Promise<WebApiResponse<MagensaSettings>>)
            .then(response => {
                this.fetching = false;
                return response;
            }).catch(error => {
                this.fetching = false;
                applicationLogger.error(error);
                return null;
            });
    }

    async getProcessedTransactions(viewModel: ProcessedTransactionRequest): Promise<WebApiResponse<Array<Transaction>>> {
        this.fetching = true;
        await this.configureHttpClient();
        return this.httpClient.fetch("api/transactions/processed",
                {
                    method: "POST",
                    body: JSON.stringify(viewModel),
                    headers: {
                        "Content-Type": "application/json",
                        "Accept": "application/json"
                    }
                })
            .then(result => result.json() as Promise<WebApiResponse<Array<Transaction>>>)
            .then(response => {
                this.fetching = false;
                return response;
            })
            .catch(error => {
                const apiError = error as IApiError;
                applicationLogger.error(apiError.message);
                this.fetching = false;
                return null;
            });
    }

    async getProcessedTransactionsByLastFour(viewModel: ProcessedTransactionByLastFourRequest): Promise<WebApiResponse<Array<Transaction>>> {
        this.fetching = true;
        await this.configureHttpClient();
        return this.httpClient.fetch("api/transactions/processed/lookup",
            {
                method: "POST",
                body: JSON.stringify(viewModel),
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json"
                }
            })
            .then(result => result.json() as Promise<WebApiResponse<Array<Transaction>>>)
            .then(response => {
                this.fetching = false;
                return response;
            })
            .catch(error => {
                const apiError = error as IApiError;
                applicationLogger.error(apiError.message);
                this.fetching = false;
                return null;
            });
    }

    async getProcessedTransactionsCsv(viewModel: ProcessedTransactionRequest, kiosk: Kiosk): Promise<WebApiResponse<void>> {
        this.fetching = true;
        await this.configureHttpClient();
        return this.httpClient.fetch("api/transactions/processed/csv",
                {
                    method: "POST",
                    body: JSON.stringify(viewModel),
                    headers: {
                        "Content-Type": "application/json",
                        "Accept": "text/csv"
                    }
                })
            .then(result => result.blob())
            .then(blob => this.downloadBlob(blob, `${kiosk.name} ${moment.utc(viewModel.startDate).local().format('YYYY-MM-DD')} to ${moment.utc(viewModel.endDate).local().add(-1, "days").format('YYYY-MM-DD')}.csv`))
            .then(response => {
                this.fetching = false;
                var webApiResponse = new WebApiResponse<void>();
                webApiResponse.success = true;
                return webApiResponse;
            })
            .catch(error => {
                const apiError = error as IApiError;
                applicationLogger.error(apiError.message);
                this.fetching = false;
                var webApiResponse = new WebApiResponse<void>();
                webApiResponse.success = true;
                webApiResponse.message = apiError.message;

                return webApiResponse;
            });
    }

    async getTransactionBatchesByDates(viewModel: TransactionBatchesRequest): Promise<WebApiResponse<Array<TransactionBatch>>> {
        this.fetching = true;
        await this.configureHttpClient();
        return this.httpClient.post(`api/tenants/management/transactionbatches`, JSON.stringify(viewModel))
            .then(result => result.json() as Promise<WebApiResponse<Array<TransactionBatch>>>)
            .then(response => {
                this.fetching = false;
                return response;
            }).catch(error => {
                this.fetching = false;
                applicationLogger.error(error);
                return null;
            });

    }

    async getTenantManagementData(tenantId: string): Promise<WebApiResponse<TenantManagement>> {
        this.fetching = true;
        await this.configureHttpClient();
        return this.httpClient.get(`api/tenants/management/${tenantId}`)
            .then(result => result.json() as Promise<WebApiResponse<TenantManagement>>)
            .then(response => {
                this.fetching = false;
                return response;
            }).catch(error => {
                this.fetching = false;
                applicationLogger.error(error);
                return null;
            });
    }

    async getTenants(): Promise<WebApiResponse<Array<Tenant>>> {
        this.fetching = true;
        await this.configureHttpClient();
        return this.httpClient.get(`api/tenants`)
            .then(result => result.json() as Promise<WebApiResponse<Array<Tenant>>>)
            .then(response => {
                this.fetching = false;
                return response;
            }).catch(error => {
                this.fetching = false;
                applicationLogger.error(error);
                return null;
            });
    }

    async getTransactionBatch(batchId: string): Promise<WebApiResponse<Array<Transaction>>> {
        this.fetching = true;
        await this.configureHttpClient();
        return this.httpClient.fetch(`api/transactions/batch/${batchId}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Accept": "application/json"
                    }
                })
            .then(result => result.json() as Promise<WebApiResponse<Array<Transaction>>>)
            .then(response => {
                this.fetching = false;
                return response;
            })
            .catch(error => {
                const apiError = error as IApiError;
                applicationLogger.error(apiError.message);
                this.fetching = false;
                return null;
            });
    }

    async getTransactionBatches(tenantId: string): Promise<WebApiResponse<Array<TransactionBatch>>> {
        this.fetching = true;
        await this.configureHttpClient();
        return this.httpClient.fetch(`api/transactions/batches/${tenantId}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Accept": "application/json"
                    }
                })
            .then(result => result.json() as Promise<WebApiResponse<Array<TransactionBatch>>>)
            .then(response => {
                this.fetching = false;
                return response;
            })
            .catch(error => {
                const apiError = error as IApiError;
                applicationLogger.error(apiError.message);
                this.fetching = false;
                return null;
            });
    }

    async getTransactionDetail(transactionId: string): Promise<WebApiResponse<TransactionDetail>> {
        this.fetching = true;
        await this.configureHttpClient();
        return this.httpClient.get(`api/transactions/${transactionId}`)
            .then(result => result.json() as Promise<WebApiResponse<TransactionDetail>>)
            .then(response => {
                this.fetching = false;
                return response;
            }).catch(error => {
                this.fetching = false;
                applicationLogger.error(error);
                return null;
            });
    }

    async logout(): Promise<WebApiResponse<void>>
    {
        this.configureHttpClient();
        return this.httpClient.post(`account/logout`)
           // .then(result => result.json() as Promise<WebApiResponse<void>>)
            .then(response =>
            {
                this.fetching = false;
                return new WebApiResponse<void>();
            }).catch(error =>
            {
                this.fetching = false;
                applicationLogger.error(error);
                return null;
            });
    }

    async processRefund(transactionId: string, refundAmount: string): Promise<WebApiResponse<Transaction>> {
        this.fetching = true;
        await this.configureHttpClient();
        return this.httpClient.get(`api/magensa/refund/${transactionId}?amount=${refundAmount}`)
            .then(result => result.json() as Promise<WebApiResponse<Transaction>>)
            .then(response => {
                this.fetching = false;
                return response;
            }).catch(error => {
                this.fetching = false;
                applicationLogger.error(error);
                return null;
            });
    }

    async processTransaction(transactionId: string): Promise<WebApiResponse<Transaction>> {
        this.fetching = true;
        await this.configureHttpClient();
        return this.httpClient.get(`api/transactions/enqueue/${transactionId}`)
            .then(result => result.json() as Promise<WebApiResponse<Transaction>>)
            .then(response => {
                this.fetching = false;
                return response;
            }).catch(error => {
                this.fetching = false;
                applicationLogger.error(error);
                return null;
            });
    }

    async resubmitTransactions(tenantId: string, startDate: string, endDate: string): Promise<WebApiResponse<number>> {
        this.fetching = true;
        await this.configureHttpClient();
        return this.httpClient.get(`api/transactions/resubmit?tenantId=${tenantId}&startDate=${moment(startDate).format('YYYY-MM-DD')}&endDate=${moment(endDate).format('YYYY-MM-DD')}`)
            .then(result => result.json() as Promise<WebApiResponse<number>>)
            .then(response => {
                this.fetching = false;
                return response;
            }).catch(error => {
                this.fetching = false;
                applicationLogger.error(error);
                return null;
            });
    }

    updateKiosk(kiosk: Kiosk): Promise<WebApiResponse<Kiosk>> {
        this.configureHttpClient();
        return this.httpClient.patch(`api/tenant/{kiosk.tenantId}/kiosk`, JSON.stringify(kiosk))
            .then(result => result.json() as Promise<WebApiResponse<Kiosk>>)
            .then(response => {
                this.fetching = false;
                return response; 
            }).catch(error => {
                this.fetching = false;
                applicationLogger.error(error);
                return null;
            });
    }

    getKioskClientSecrets(kioskClientSecretsRequest: KioskClientSecretsRequest): Promise<WebApiResponse<KioskClientSecretsResponse>> {
        this.configureHttpClient();
        return this.httpClient.patch(`api/kiosk/{kioskClientSecretsRequest.kioskId}/secrets`, JSON.stringify(kioskClientSecretsRequest))
            .then(result => result.json() as Promise<WebApiResponse<KioskClientSecretsResponse>>)
            .then(response => {
                this.fetching = false;
                return response;
            }).catch(error => {
                this.fetching = false;
                applicationLogger.error(error);
                return null;
            });
    }

    updatePaymentSettings(kioskId: string, magensaSettings: MagensaSettings): Promise<WebApiResponse<MagensaSettings>> {
        this.configureHttpClient();
        return this.httpClient.post(`api/magensa/kiosk/${kioskId}`, JSON.stringify(magensaSettings))
            .then(result => result.json() as Promise<WebApiResponse<MagensaSettings>>)
            .then(response => {
                this.fetching = false;
                return response;
            }).catch(error => {
                this.fetching = false;
                applicationLogger.error(error);
                return null;
            });
    }

    async updateTransactionStatus(transactionId: string, status: TransactionStatus): Promise<WebApiResponse<TransactionStatus>> {
        this.fetching = true;
        await this.configureHttpClient();
        return this.httpClient.post(`api/transactions/status/${transactionId}?status=${status}`, "")
            .then(result => result.json() as Promise<WebApiResponse<TransactionStatus>>)
            .then(response => {
                this.fetching = false;
                return response;
            }).catch(error => {
                this.fetching = false;
                applicationLogger.error(error);
                return null;
            });
    }
}